import { useCallback, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { Download, RefreshCw } from 'lucide-react';
import { Button } from './ui/Button';
import {
  checkForAppUpdate,
  dismissUpdateVersion,
  hardRefreshWebApp,
} from '../utils/appVersion';
import { openPlayStore } from '../utils/openPlayStore';

export function AppUpdatePrompt({ labels }) {
  const [update, setUpdate] = useState(null);
  const [busy, setBusy] = useState(false);

  const runCheck = useCallback(async () => {
    try {
      const result = await checkForAppUpdate();
      if (result.kind === 'android' || result.kind === 'web') {
        setUpdate(result);
      } else {
        setUpdate(null);
      }
    } catch (err) {
      console.warn('App update check failed:', err);
    }
  }, []);

  useEffect(() => {
    void runCheck();

    const onVisible = () => {
      if (document.visibilityState === 'visible') void runCheck();
    };
    document.addEventListener('visibilitychange', onVisible);

    let handle;
    if (Capacitor.getPlatform() === 'android') {
      App.addListener('appStateChange', ({ isActive }) => {
        if (isActive) void runCheck();
      }).then((h) => {
        handle = h;
      });
    }

    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      handle?.remove();
    };
  }, [runCheck]);

  if (!update?.kind) return null;

  const onLater = () => {
    if (update.dismissToken) dismissUpdateVersion(update.dismissToken);
    setUpdate(null);
  };

  const onAndroidUpdate = async () => {
    setBusy(true);
    try {
      await openPlayStore({
        marketUrl: update.marketUrl,
        storeUrl: update.storeUrl,
      });
    } finally {
      setBusy(false);
    }
  };

  const onWebRefresh = async () => {
    setBusy(true);
    try {
      await hardRefreshWebApp();
    } catch (err) {
      console.warn('Hard refresh failed:', err);
      window.location.reload();
    }
  };

  const isWeb = update.kind === 'web';
  const body = isWeb ? labels.appUpdateWebBody : labels.appUpdateAndroidBody;
  const installedVersion = isWeb ? update.localVersion : update.nativeVersion;
  const installedLabel = installedVersion
    ? (labels.appUpdateInstalledVersion || 'Installed: v{v}').replace('{v}', installedVersion)
    : null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center p-4 sm:items-center">
      <div className="absolute inset-0 bg-black/50" aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="app-update-title"
        className="relative z-10 w-full max-w-md rounded-2xl border border-border bg-card p-5 shadow-card"
      >
        <div className="mb-4 flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary-light text-primary">
            {isWeb ? (
              <RefreshCw className="h-5 w-5" aria-hidden />
            ) : (
              <Download className="h-5 w-5" aria-hidden />
            )}
          </div>
          <div className="min-w-0">
            <h2 id="app-update-title" className="text-lg font-semibold text-text">
              {labels.appUpdateTitle}
            </h2>
            <p className="mt-1 text-sm text-muted">
              {body}
              {update.remoteVersion ? ` (v${update.remoteVersion})` : ''}
            </p>
            {installedLabel ? (
              <p className="mt-1 text-xs text-muted">{installedLabel}</p>
            ) : null}
          </div>
        </div>

        {update.notes ? (
          <p className="mb-4 rounded-lg bg-surface px-3 py-2 text-sm text-text">{update.notes}</p>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
          {!update.force ? (
            <Button variant="ghost" className="w-full sm:w-auto" onClick={onLater} disabled={busy}>
              {labels.appUpdateDismiss}
            </Button>
          ) : null}
          {isWeb ? (
            <Button className="w-full sm:w-auto" onClick={onWebRefresh} isLoading={busy}>
              {labels.appUpdateHardRefresh || labels.appUpdateRefresh}
            </Button>
          ) : (
            <Button className="w-full sm:w-auto" onClick={onAndroidUpdate} isLoading={busy}>
              {labels.appUpdatePlayStore}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
